import { ShadcnQueryProps } from '@shadcn-features/query';
import { QueryKey } from '@tanstack/react-query';
import { Loader2Icon } from 'lucide-react';
import { useMemo } from 'react';
import { useQueryFetch } from '@shared/hooks/useQueryFetch';
import { DefaultOptionType, ResponseType } from '@shared/types/types';

export interface RenderOptionsProps<
    TResponseData,
    TSelectData extends Array<DefaultOptionType>,
    TParams extends Record<string, unknown> = Record<string, unknown>,
    TBody = unknown,
> {
    value: unknown;
    options?: TSelectData;
    query?: ShadcnQueryProps<
        ResponseType<TResponseData>,
        TParams,
        TBody,
        TResponseData,
        TSelectData
    >;
    children?: (option: TSelectData) => React.ReactNode;
}
export function RenderOptions<
    TResponseData,
    TSelectData extends Array<DefaultOptionType> = Array<DefaultOptionType>,
    TParams extends Record<string, unknown> = Record<string, unknown>,
    TBody = unknown,
>({
    query,
    value,
    options: propsOptions,
    children,
}: RenderOptionsProps<TResponseData, TSelectData, TParams, TBody>) {
    const { isFetching, data: fetchedOptions } = useQueryFetch(
        query?.url || '',
        {
            ...query,
            enabled: !!query?.url,
        },
    );

    const options = useMemo(
        () => propsOptions ?? fetchedOptions,
        [fetchedOptions, propsOptions],
    );

    const foundOptions = useMemo(() => {
        return options?.filter((option) => {
            if (Array.isArray(value)) {
                return value.includes(option.value);
            }

            return value === option.value;
        }) as TSelectData | undefined;
    }, [options, value]);

    const resolvedLabel =
        foundOptions?.map((option) => option.label).join(', ') || value;

    return (
        <>
            {isFetching && (
                <Loader2Icon className='inline-block animate-spin' />
            )}
            {foundOptions
                ? (children?.(foundOptions) ?? resolvedLabel)
                : resolvedLabel}
        </>
    );
}
